import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { openProtectedFile } from "@/lib/openFile";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { StatusPill } from "./DashboardLayout";
import { toast } from "sonner";
import { Trash2, Eye, Mail, Calendar, Stethoscope, ShieldCheck } from "lucide-react";

const ROLES = ["doctor", "admin"];
const STATUSES = ["none", "pending", "approved", "rejected"];

function UserDetailDialog({ userId, open, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    setError(null);
    api.get(`/admin/users/${userId}/detail`)
      .then((r) => setDetail(r.data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const RoleIcon = detail?.user?.role === "admin" ? ShieldCheck : Stethoscope;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">User profile</DialogTitle>
        </DialogHeader>

        {loading && <div className="text-sm text-muted-foreground py-6">Loading…</div>}
        {error && <div className="text-sm text-destructive py-3">{error}</div>}

        {detail && (
          <div className="space-y-6">
            <div className="border border-border bg-secondary/30 p-5">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="w-14 h-14 rounded-full bg-white border border-border grid place-items-center shrink-0">
                  {detail.user.picture ? (
                    <img src={detail.user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <RoleIcon className="w-6 h-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl font-semibold">{detail.user.name}</div>
                  <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                    <Mail className="w-3.5 h-3.5" />
                    <span className="break-all">{detail.user.email}</span>
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Registered {new Date(detail.user.created_at).toLocaleString()}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-1">{detail.user.user_id}</div>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-foreground text-white rounded-md">
                    {detail.user.role}
                  </span>
                  {detail.user.auth_provider === "google" && (
                    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-white border border-border rounded-md text-muted-foreground">
                      Google
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border border-border bg-white p-4">
                <div className="overline">Orders</div>
                <div className="font-display text-2xl font-bold mt-1">{detail.orders_count}</div>
              </div>
              <div className="border border-border bg-white p-4">
                <div className="overline">Total spent</div>
                <div className="font-display text-2xl font-bold mt-1">${detail.total_spent.toFixed(2)}</div>
              </div>
              <div className="border border-border bg-white p-4">
                <div className="overline">ID status</div>
                <div className="mt-2"><StatusPill status={detail.user.id_document_status || "none"} /></div>
              </div>
              <div className="border border-border bg-white p-4">
                <div className="overline">License status</div>
                <div className="mt-2"><StatusPill status={detail.user.license_status || "none"} /></div>
              </div>
            </div>

            {detail.user.role === "doctor" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border border-border bg-white p-4">
                  <div className="overline">License number</div>
                  <div className="font-mono text-sm mt-1">{detail.user.doctor_license || "—"}</div>
                </div>
                <div className="border border-border bg-white p-4">
                  <div className="overline">Specialty</div>
                  <div className="text-sm mt-1">{detail.user.specialty || "—"}</div>
                </div>
                <div className="border border-border bg-white p-4">
                  <div className="overline">WhatsApp</div>
                  {detail.user.whatsapp ? (
                    <a
                      href={`https://wa.me/${detail.user.whatsapp.replace(/[^\d]/g, "")}`}
                      target="_blank" rel="noopener noreferrer"
                      className="font-mono text-sm mt-1 underline text-[hsl(var(--accent))] block break-all"
                      data-testid="user-whatsapp-link"
                    >
                      {detail.user.whatsapp}
                    </a>
                  ) : (
                    <div className="font-mono text-sm mt-1 text-muted-foreground">—</div>
                  )}
                </div>
              </div>
            )}

            <div>
              <div className="overline mb-2">Identity documents ({detail.id_documents.length})</div>
              {detail.id_documents.length === 0 ? (
                <div className="border border-border bg-white p-4 text-sm text-muted-foreground">None.</div>
              ) : (
                <div className="border border-border bg-white divide-y divide-border">
                  {detail.id_documents.map((d) => (
                    <div key={d.id_document_id} className="p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm truncate">{d.original_filename}</div>
                        <div className="text-xs text-muted-foreground">
                          {d.document_type} · {new Date(d.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusPill status={d.status} />
                        <button
                          type="button"
                          onClick={() => openProtectedFile(`/identity/${d.id_document_id}/file`)}
                          className="text-xs underline text-[hsl(var(--accent))]"
                          data-testid={`open-id-${d.id_document_id}`}
                        >
                          View →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="overline mb-2">Medical licenses ({detail.licenses.length})</div>
              {detail.licenses.length === 0 ? (
                <div className="border border-border bg-white p-4 text-sm text-muted-foreground">None.</div>
              ) : (
                <div className="border border-border bg-white divide-y divide-border">
                  {detail.licenses.map((l) => (
                    <div key={l.license_document_id} className="p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm truncate">{l.original_filename}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(l.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusPill status={l.status} />
                        <button
                          type="button"
                          onClick={() => openProtectedFile(`/licenses/${l.license_document_id}/file`)}
                          className="text-xs underline text-[hsl(var(--accent))]"
                          data-testid={`open-license-${l.license_document_id}`}
                        >
                          View →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="overline mb-2">Orders ({detail.orders.length})</div>
              {detail.orders.length === 0 ? (
                <div className="border border-border bg-white p-4 text-sm text-muted-foreground">No orders yet.</div>
              ) : (
                <div className="border border-border bg-white divide-y divide-border">
                  {detail.orders.map((o) => (
                    <div key={o.order_id} className="p-3">
                      <div className="flex justify-between items-center gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="font-mono text-xs text-muted-foreground">{o.order_id}</div>
                          <div className="text-sm font-medium mt-0.5">
                            ${o.total.toFixed(2)} · {o.items.length} item(s)
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(o.created_at).toLocaleString()}
                          </div>
                        </div>
                        <StatusPill status={o.status} />
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        {o.items.map((i) => `${i.name} ×${i.quantity}`).join(" · ")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState(null);
  const [detailUserId, setDetailUserId] = useState(null);

  const load = () => {
    const url = filter === "all" ? "/admin/users" : `/admin/users?role=${filter}`;
    api.get(url).then((r) => setUsers(r.data));
  };
  useEffect(() => { load(); }, [filter]);

  const setRole = async (user_id, role) => {
    try {
      await api.put(`/admin/users/${user_id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const setIdStatus = async (user_id, status) => {
    try {
      await api.put(`/admin/users/${user_id}/id-status`, { status });
      toast.success("ID status updated");
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const setLicenseStatus = async (user_id, status) => {
    try {
      await api.put(`/admin/users/${user_id}/license-status`, { status });
      toast.success("License status updated");
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-users" className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="overline">User management</div>
          <h2 className="font-display text-2xl font-semibold mt-1">Users</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "doctor", "admin"].map((f) => (
            <button
              key={f}
              data-testid={`users-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 h-9 text-sm border rounded-md whitespace-nowrap ${
                filter === f ? "bg-foreground text-white border-foreground" : "bg-white border-border text-muted-foreground"
              }`}
            >{f}</button>
          ))}
        </div>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}

      {users.length === 0 ? (
        <div className="border border-border bg-white p-8 text-sm text-muted-foreground text-center">
          No users in this filter.
        </div>
      ) : (
        <div className="border border-border bg-white divide-y divide-border">
          {users.map((u) => (
            <div key={u.user_id} className="p-4 grid grid-cols-1 md:grid-cols-12 items-start md:items-center gap-2 md:gap-3">
              <div className="md:col-span-3">
                <div className="font-display font-semibold">{u.name}</div>
                <div className="text-xs text-muted-foreground break-all">{u.email}</div>
                <div className="font-mono text-[10px] text-muted-foreground mt-1">{u.user_id}</div>
              </div>
              <div className="md:col-span-2">
                <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)}>
                  <SelectTrigger className="h-9" data-testid={`role-${u.user_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <div className="overline mb-1">ID</div>
                {u.role === "doctor" ? (
                  <Select value={u.id_document_status || "none"} onValueChange={(v) => setIdStatus(u.user_id, v)}>
                    <SelectTrigger className="h-9" data-testid={`id-${u.user_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <StatusPill status={u.id_document_status || "none"} />
                )}
              </div>
              <div className="md:col-span-3">
                <div className="overline mb-1">License</div>
                {u.role === "doctor" ? (
                  <Select value={u.license_status || "none"} onValueChange={(v) => setLicenseStatus(u.user_id, v)}>
                    <SelectTrigger className="h-9" data-testid={`license-${u.user_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <StatusPill status={u.license_status || "none"} />
                )}
              </div>
              <div className="md:col-span-1 flex md:justify-end gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDetailUserId(u.user_id)}
                  data-testid={`view-user-${u.user_id}`}
                  title="View details"
                >
                  <Eye className="w-4 h-4" />
                </Button>
                {u.user_id !== me?.user_id && (
                  <Button size="sm" variant="ghost" onClick={() => remove(u)} data-testid={`del-user-${u.user_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <UserDetailDialog
        userId={detailUserId}
        open={!!detailUserId}
        onClose={() => setDetailUserId(null)}
      />
    </div>
  );
}
